import React, { useState, useEffect, useMemo, useRef, useId } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContentInline,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Plus,
  RotateCcw,
  Store,
  X,
} from 'lucide-react';
import {
  COUNTRIES,
  COUNTRY_NAME_BY_CODE,
  COMMON_COUNTRY_CODES,
  CITY_TIMEZONE_OVERRIDES,
  resolveTimezoneForLocation,
} from '@threadheads/shared';
import { useLocationMutations } from '../hooks/useLocationMutations';
import type { Location } from '../types';
import { locationNameTakenMessage } from '../utils/locationNameTaken';
import { shopifyLocationConnectedMessage } from '../utils/shopifyLocationConnected';
// BLU-495 Q4: `Switch` import removed when the enable_auto_availability toggle was hidden.
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
// Common operational timezones — always available in the combobox's "Common"
// group and used as a fallback if Intl.supportedValuesOf isn't supported.
// Hoisted to lib/timezones.ts (BLU-734) so the Portless digest schedule picker
// offers the identical set.
import { COMMON_TIMEZONES } from '@/lib/timezones';
import { TickingClock } from './TickingClock';

interface LocationFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: Location | null;
}

const getAllTimezones = (): string[] => {
  try {
    const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf?.('timeZone');
    if (Array.isArray(all) && all.length > 0) return all;
  } catch {
    // fall through
  }
  return COMMON_TIMEZONES;
};

// Humanise an IANA zone for display: last path segment, underscores → spaces.
// "America/Argentina/Buenos_Aires" → "Buenos Aires".
const humanizeZoneCity = (iana: string): string => {
  const parts = iana.split('/');
  const last = parts[parts.length - 1] ?? iana;
  return last.replace(/_/g, ' ');
};

// Short GMT offset for a zone. Computed once per zone and cached — offsets are
// stable for the current moment and we never display second-precision data.
const zoneOffsetCache = new Map<string, string>();
const getZoneOffset = (iana: string): string => {
  const cached = zoneOffsetCache.get(iana);
  if (cached) return cached;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    const offset = name && name !== 'GMT' ? name.replace('GMT', 'GMT') : 'GMT±00:00';
    zoneOffsetCache.set(iana, offset);
    return offset;
  } catch {
    zoneOffsetCache.set(iana, '');
    return '';
  }
};

const DEFAULT_FORM = {
  name: '',
  shopify_location_id: '',
  city: '',
  country_code: 'US',
  timezone: 'America/New_York',
  // BLU-495 Q4: column is inert post-back-sync deprecation. New locations
  // default to false so the cleanup migration (BLU-XXX-cleanup-availability-
  // vestigial) doesn't inherit semantically meaningless 'true' values. The UI
  // toggle is hidden (see line ~822). Edits preserve existing values.
  enable_auto_availability: false,
  // BLU-581 — warehouse address, contact, and customs details. All optional;
  // empty strings are normalised to null (and [] for emails) on submit.
  address_line1: '',
  address_line2: '',
  state_province: '',
  postal_code: '',
  contact_phone: '',
  contact_emails: [] as string[],
  customs_info: '',
};

// Validate against the SAME rule the backend enforces (packages/shared
// locations schema uses z.string().trim().email()), so a chip the UI accepts
// can never be rejected by the server and fail the whole location save.
const isValidEmail = (value: string): boolean =>
  z.string().email().safeParse(value).success;
const MAX_CONTACT_EMAILS = 20;

// Empty text field means "unset" — store null, never an empty string.
const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// Lightweight in-modal section divider. Groups the form into Address / Contact
// / Customs without competing with the primary field labels.
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {children}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

type ShopifyFillSnapshot = {
  name: string;
  city: string;
  country_code: string;
} | null;

// What triggered the last auto-detect. Feeds the "From {city}" vs "From
// {country}" label and the aria-live announcement.
type DetectionSource =
  | { kind: 'city'; city: string }
  | { kind: 'country'; countryCode: string }
  | null;

export default function LocationFormModal({ open, onOpenChange, location }: LocationFormModalProps) {
  const isEditing = !!location;
  const { createLocation, updateLocation } = useLocationMutations();

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [countryOpen, setCountryOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [shopifyPickerOpen, setShopifyPickerOpen] = useState(false);
  const [selectedShopifyId, setSelectedShopifyId] = useState<number | null>(null);
  const [linkedWarning, setLinkedWarning] = useState<string | null>(null);
  const [shopifyFill, setShopifyFill] = useState<ShopifyFillSnapshot>(null);
  const [announce, setAnnounce] = useState('');
  const [timezoneManuallySet, setTimezoneManuallySet] = useState(false);
  const [isDetectingTz, setIsDetectingTz] = useState(false);
  const [detectionSource, setDetectionSource] = useState<DetectionSource>(null);
  // City value debounced for auto-detect. Keystroke-level `formData.city`
  // stays responsive; auto-detect waits for the user to pause typing.
  const [debouncedCity, setDebouncedCity] = useState('');
  // BLU-581 — pending contact-email entry + inline validation error.
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  // Set when the server refuses the name because the org already has a
  // location with it; cleared as soon as the name changes or a new save starts.
  const [nameError, setNameError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Same, for a Shopify location already connected to a partner warehouse;
  // cleared when the Shopify location changes or a new save starts.
  const [shopifyIdError, setShopifyIdError] = useState<string | null>(null);
  const shopifyIdInputRef = useRef<HTMLInputElement>(null);

  const shopifyListboxId = useId();
  const countryListboxId = useId();
  const timezoneListboxId = useId();
  const emailErrorId = useId();
  const nameErrorId = useId();
  const shopifyIdErrorId = useId();
  const emailHelpId = useId();

  useEffect(() => {
    setNameError(null);
    setShopifyIdError(null);
    if (location) {
      setFormData({
        name: location.name,
        shopify_location_id: location.shopify_location_id.toString(),
        city: location.city,
        country_code: location.country_code,
        timezone: location.timezone,
        enable_auto_availability: location.enable_auto_availability,
        address_line1: location.address_line1 ?? '',
        address_line2: location.address_line2 ?? '',
        state_province: location.state_province ?? '',
        postal_code: location.postal_code ?? '',
        contact_phone: location.contact_phone ?? '',
        contact_emails: location.contact_emails ?? [],
        customs_info: location.customs_info ?? '',
      });
      setSelectedShopifyId(location.shopify_location_id);
      setShopifyFill(null);
      setDebouncedCity(location.city);
      setEmailInput('');
      setEmailError(null);
      // Existing location starts with a chosen timezone, but we no longer
      // lock it — if the user changes country/city mid-edit, auto-detect
      // should still offer to update. See onSelect handlers below.
      setTimezoneManuallySet(true);
      setDetectionSource(null);
    } else {
      setFormData(DEFAULT_FORM);
      setSelectedShopifyId(null);
      setShopifyFill(null);
      setLinkedWarning(null);
      setTimezoneManuallySet(false);
      setDetectionSource(null);
      setDebouncedCity('');
      setEmailInput('');
      setEmailError(null);
    }
  }, [location, open]);

  // Debounce city so the auto-detect effect doesn't fire on every keystroke
  // and flicker the trigger label while the user is still typing.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedCity(formData.city), 400);
    return () => window.clearTimeout(handle);
  }, [formData.city]);

  // Derive the timezone that should be active based on country + debounced
  // city. Pure — doesn't touch state. Also tells us WHY it was picked so we
  // can render "From {city}" vs "From {country}".
  const derivedTz = useMemo((): { tz: string | null; source: DetectionSource } => {
    if (!formData.country_code) return { tz: null, source: null };
    const code = formData.country_code.toUpperCase();
    const cityLookup =
      debouncedCity &&
      CITY_TIMEZONE_OVERRIDES[code]?.[debouncedCity.trim().toLowerCase()];
    if (cityLookup) {
      return { tz: cityLookup, source: { kind: 'city', city: debouncedCity.trim() } };
    }
    const countryTz = resolveTimezoneForLocation(formData.country_code, null);
    if (countryTz) {
      return { tz: countryTz, source: { kind: 'country', countryCode: code } };
    }
    return { tz: null, source: null };
  }, [formData.country_code, debouncedCity]);

  // Apply auto-detect when the derived value actually differs from the
  // currently-committed timezone and the user hasn't picked one manually.
  useEffect(() => {
    if (timezoneManuallySet) return;
    if (!derivedTz.tz) return;
    if (derivedTz.tz === formData.timezone) return;

    setIsDetectingTz(true);
    const handle = window.setTimeout(() => {
      setFormData((prev) =>
        prev.timezone === derivedTz.tz ? prev : { ...prev, timezone: derivedTz.tz! }
      );
      setDetectionSource(derivedTz.source);
      setIsDetectingTz(false);
      // Humanise for screen readers — IANA strings get read as "slash".
      const friendly = humanizeZoneCity(derivedTz.tz!);
      const offset = getZoneOffset(derivedTz.tz!);
      const sourceLabel =
        derivedTz.source?.kind === 'city'
          ? derivedTz.source.city
          : COUNTRY_NAME_BY_CODE[
              (derivedTz.source as { countryCode: string } | null)?.countryCode ?? ''
            ] ?? 'country';
      setAnnounce(`Timezone set to ${friendly} ${offset} from ${sourceLabel}.`);
    }, 350);

    return () => {
      window.clearTimeout(handle);
      setIsDetectingTz(false);
    };
  }, [derivedTz.tz, derivedTz.source, formData.timezone, timezoneManuallySet]);

  const shopifyLocationsQuery = trpc.locations.listShopifyLocations.useQuery(undefined, {
    enabled: open && !isEditing,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const shopifyLocations = shopifyLocationsQuery.data?.locations ?? [];
  const shopifyLocationsHasMore = shopifyLocationsQuery.data?.hasMore ?? false;
  const shopifyError = shopifyLocationsQuery.error;

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === formData.country_code),
    [formData.country_code]
  );

  const { commonCountries, otherCountries } = useMemo(() => {
    const commonSet = new Set<string>(COMMON_COUNTRY_CODES);
    const byCode = new Map(COUNTRIES.map((c) => [c.code, c]));
    const common = COMMON_COUNTRY_CODES.map((code) => byCode.get(code)).filter(
      (c): c is NonNullable<typeof c> => !!c
    );
    const others = COUNTRIES.filter((c) => !commonSet.has(c.code));
    return { commonCountries: common, otherCountries: others };
  }, []);

  const { commonTimezones, otherTimezones } = useMemo(() => {
    const all = getAllTimezones();
    const commonSet = new Set(COMMON_TIMEZONES);
    const common = COMMON_TIMEZONES.filter((tz) => all.includes(tz));
    const others = all.filter((tz) => !commonSet.has(tz)).sort();
    return { commonTimezones: common, otherTimezones: others };
  }, []);

  const clearShopifySelection = () => {
    setSelectedShopifyId(null);
    setShopifyFill(null);
    setLinkedWarning(null);
    setShopifyIdError(null);
    setFormData((prev) => ({ ...prev, shopify_location_id: '' }));
  };

  const handleShopifyLocationSelect = (shopifyId: number) => {
    const picked = shopifyLocations.find((l) => l.shopifyLocationId === shopifyId);
    if (!picked) return;

    if (picked.alreadyLinked) {
      setLinkedWarning(
        `${picked.name} is already linked to ${
          picked.linkedToLocationName ? `“${picked.linkedToLocationName}”` : 'another location'
        } in this workspace. Pick another, or edit the existing location.`
      );
      return;
    }

    setLinkedWarning(null);
    setNameError(null);
    setShopifyIdError(null);
    setSelectedShopifyId(picked.shopifyLocationId);
    const nextCountry =
      picked.countryCode && COUNTRY_NAME_BY_CODE[picked.countryCode]
        ? picked.countryCode
        : formData.country_code;
    const nextName = picked.name || formData.name;
    const nextCity = picked.city || formData.city;

    setTimezoneManuallySet(false);
    setFormData((prev) => ({
      ...prev,
      name: nextName,
      shopify_location_id: String(picked.shopifyLocationId),
      city: nextCity,
      country_code: nextCountry,
    }));
    setDebouncedCity(nextCity);
    setShopifyFill({ name: nextName, city: nextCity, country_code: nextCountry });
    setShopifyPickerOpen(false);
    setAnnounce(`Imported ${picked.name} from Shopify. Filled name, city, and country.`);
  };

  const handleResetField = (field: 'name' | 'city' | 'country_code') => {
    if (!shopifyFill) return;
    if (field === 'name') setNameError(null);
    setFormData((prev) => ({ ...prev, [field]: shopifyFill[field] }));
    if (field === 'country_code' || field === 'city') {
      setTimezoneManuallySet(false);
    }
    if (field === 'city') setDebouncedCity(shopifyFill.city);
  };

  const fieldIsModified = (field: 'name' | 'city' | 'country_code') =>
    !!shopifyFill && formData[field] !== shopifyFill[field];

  const handleCreateManually = () => {
    clearShopifySelection();
    setShopifyPickerOpen(false);
    window.setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  // Validate the pending email input against the current chips. Returns the
  // resolved emails array (unchanged when the input is empty or a duplicate),
  // or an error string to surface. Shared by the Add button and submit-flush.
  const resolvePendingEmail = (): { emails: string[] } | { error: string } => {
    const value = emailInput.trim();
    if (!value) return { emails: formData.contact_emails };
    if (!isValidEmail(value)) return { error: 'Enter a valid email address.' };
    if (formData.contact_emails.includes(value)) return { emails: formData.contact_emails };
    if (formData.contact_emails.length >= MAX_CONTACT_EMAILS) {
      return { error: `You can add up to ${MAX_CONTACT_EMAILS} contact emails.` };
    }
    return { emails: [...formData.contact_emails, value] };
  };

  const handleAddEmail = () => {
    const result = resolvePendingEmail();
    if ('error' in result) {
      setEmailError(result.error);
      return;
    }
    setFormData((prev) => ({ ...prev, contact_emails: result.emails }));
    setEmailInput('');
    setEmailError(null);
  };

  const handleRemoveEmail = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      contact_emails: prev.contact_emails.filter((e) => e !== email),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const shopifyIdNum = parseInt(formData.shopify_location_id, 10);
    if (isNaN(shopifyIdNum)) return;

    // Flush any valid email still sitting in the input the user never pressed
    // Add/Enter on. An invalid pending value aborts the submit rather than
    // silently dropping it or shipping a bad address.
    const emailResult = resolvePendingEmail();
    if ('error' in emailResult) {
      setEmailError(emailResult.error);
      return;
    }
    const contactEmails = emailResult.emails;
    if (contactEmails !== formData.contact_emails) {
      setFormData((prev) => ({ ...prev, contact_emails: contactEmails }));
      setEmailInput('');
      setEmailError(null);
    }

    setNameError(null);
    setShopifyIdError(null);
    const onFieldError = (error: unknown) => {
      const nameSentence = locationNameTakenMessage(error);
      if (nameSentence) setNameError(nameSentence);
      const shopifySentence = shopifyLocationConnectedMessage(error);
      if (shopifySentence) setShopifyIdError(shopifySentence);
    };

    if (isEditing && location) {
      updateLocation.mutate(
        {
          locationId: location.id,
          updates: {
            name: formData.name,
            shopify_location_id: shopifyIdNum,
            city: formData.city,
            country_code: formData.country_code,
            timezone: formData.timezone,
            enable_auto_availability: formData.enable_auto_availability,
            address_line1: trimOrNull(formData.address_line1),
            address_line2: trimOrNull(formData.address_line2),
            state_province: trimOrNull(formData.state_province),
            postal_code: trimOrNull(formData.postal_code),
            contact_phone: trimOrNull(formData.contact_phone),
            contact_emails: contactEmails,
            customs_info: trimOrNull(formData.customs_info),
          },
        },
        { onSuccess: () => onOpenChange(false), onError: onFieldError }
      );
    } else {
      createLocation.mutate(
        {
          name: formData.name,
          shopify_location_id: shopifyIdNum,
          city: formData.city,
          country_code: formData.country_code,
          timezone: formData.timezone,
          enable_auto_availability: formData.enable_auto_availability,
          address_line1: trimOrNull(formData.address_line1),
          address_line2: trimOrNull(formData.address_line2),
          state_province: trimOrNull(formData.state_province),
          postal_code: trimOrNull(formData.postal_code),
          contact_phone: trimOrNull(formData.contact_phone),
          contact_emails: contactEmails,
          customs_info: trimOrNull(formData.customs_info),
        },
        { onSuccess: () => onOpenChange(false), onError: onFieldError }
      );
    }
  };

  const isLoading = createLocation.isPending || updateLocation.isPending;
  const canSubmit = !isLoading && !linkedWarning;

  // The Name input is disabled while saving, so focus waits for it to come
  // back rather than firing from the mutation callback.
  useEffect(() => {
    if (nameError && !isLoading) nameInputRef.current?.focus();
  }, [nameError, isLoading]);

  useEffect(() => {
    if (shopifyIdError && !isLoading) shopifyIdInputRef.current?.focus();
  }, [shopifyIdError, isLoading]);

  const selectedShopify = useMemo(
    () =>
      selectedShopifyId
        ? shopifyLocations.find((l) => l.shopifyLocationId === selectedShopifyId) ?? null
        : null,
    [selectedShopifyId, shopifyLocations]
  );

  const renderModifiedTag = (field: 'name' | 'city' | 'country_code', label: string) => {
    if (!fieldIsModified(field)) return null;
    return (
      <>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Modified
        </span>
        <button
          type="button"
          onClick={() => handleResetField(field)}
          className="h-4 w-4 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label={`Reset ${label} to Shopify value`}
          title="Reset to Shopify value"
        >
          <RotateCcw className="h-3 w-3" />
        </button>
      </>
    );
  };

  // Label copy for the "From X" timezone tag: specific city when we hit the
  // override map, country name when we fell through to the primary.
  const timezoneSourceLabel = useMemo(() => {
    if (timezoneManuallySet || !detectionSource) return null;
    if (detectionSource.kind === 'city') {
      return `From ${detectionSource.city}`;
    }
    const name = COUNTRY_NAME_BY_CODE[detectionSource.countryCode] ?? detectionSource.countryCode;
    return `From ${name}`;
  }, [timezoneManuallySet, detectionSource]);

  const renderTimezoneRow = (tz: string) => {
    const city = humanizeZoneCity(tz);
    const offset = getZoneOffset(tz);
    return (
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm">{city}</div>
        <div className="truncate text-[11px] text-muted-foreground font-mono tabular-nums">
          {offset}
          {offset ? ' · ' : ''}
          {tz}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Location' : 'Add New Location'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the location details below.'
              : 'Pick from Shopify to auto-fill, or fill the fields manually.'}
          </DialogDescription>
        </DialogHeader>

        <span className="sr-only" aria-live="polite" role="status">
          {announce}
        </span>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-col">
          <div className="-mx-1 max-h-[65vh] space-y-4 overflow-y-auto px-1 py-4">
            {!isEditing && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <Label>Shopify location</Label>
                  <button
                    type="button"
                    onClick={handleCreateManually}
                    className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground"
                  >
                    or create manually →
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Most locations come from Shopify. Selecting one fills in the details below.
                </p>
                <Popover open={shopifyPickerOpen} onOpenChange={setShopifyPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={shopifyPickerOpen}
                      aria-controls={shopifyListboxId}
                      aria-haspopup="listbox"
                      aria-invalid={shopifyIdError !== null || undefined}
                      className={cn(
                        'w-full justify-between font-normal',
                        shopifyIdError !== null && 'border-destructive ring-1 ring-destructive'
                      )}
                      disabled={isLoading}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">
                          {selectedShopify
                            ? selectedShopify.name
                            : shopifyLocationsQuery.isLoading
                              ? 'Loading Shopify locations…'
                              : 'Search Shopify locations…'}
                        </span>
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContentInline
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search Shopify locations…" />
                      <CommandList id={shopifyListboxId}>
                        {shopifyLocationsQuery.isLoading ? (
                          <div className="flex items-center gap-2 px-3 py-6 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Loading locations…
                          </div>
                        ) : shopifyError ? (
                          <div className="p-2">
                            <Alert variant="destructive">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertDescription className="text-xs">
                                Couldn’t load Shopify locations. You can still create the location
                                manually below.
                              </AlertDescription>
                            </Alert>
                          </div>
                        ) : shopifyLocations.length === 0 ? (
                          <CommandEmpty>No Shopify locations found for this store.</CommandEmpty>
                        ) : (
                          <CommandGroup>
                            {shopifyLocations.map((loc) => (
                              <CommandItem
                                key={loc.shopifyLocationId}
                                value={`${loc.name} ${loc.city} ${loc.shopifyLocationId}`}
                                onSelect={() => handleShopifyLocationSelect(loc.shopifyLocationId)}
                                className="flex items-start gap-2"
                                aria-disabled={loc.alreadyLinked || undefined}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={cn(
                                        'font-medium truncate',
                                        loc.alreadyLinked && 'text-muted-foreground'
                                      )}
                                    >
                                      {loc.name}
                                    </span>
                                    {loc.alreadyLinked && (
                                      <span className="rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-foreground">
                                        Linked
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground font-mono tabular-nums">
                                    #{loc.shopifyLocationId}
                                    {loc.city ? ` · ${loc.city}` : ''}
                                    {loc.countryCode ? ` · ${loc.countryCode}` : ''}
                                  </div>
                                </div>
                                {selectedShopifyId === loc.shopifyLocationId && (
                                  <Check className="h-4 w-4 shrink-0" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                    {shopifyLocationsHasMore && (
                      <div className="border-t px-3 py-2 text-[10px] text-muted-foreground">
                        Showing first 50. Add more in Shopify Admin if needed.
                      </div>
                    )}
                  </PopoverContentInline>
                </Popover>
                {linkedWarning && (
                  <Alert variant="warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">{linkedWarning}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="name">Location Name *</Label>
                {renderModifiedTag('name', 'name')}
              </div>
              <Input
                id="name"
                ref={nameInputRef}
                type="text"
                placeholder="e.g., Main Warehouse, NYC Store"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  setNameError(null);
                }}
                required
                disabled={isLoading}
                aria-invalid={nameError !== null || undefined}
                aria-describedby={nameError !== null ? nameErrorId : undefined}
                className={cn(
                  nameError !== null &&
                    'border-destructive ring-1 ring-destructive focus-visible:border-destructive'
                )}
              />
              {nameError !== null && (
                <p id={nameErrorId} role="alert" className="text-sm text-destructive">
                  {nameError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="shopify_location_id">Shopify Location ID *</Label>
              <div className="relative">
                <Input
                  id="shopify_location_id"
                  ref={shopifyIdInputRef}
                  type="number"
                  placeholder="e.g., 14504394845"
                  value={formData.shopify_location_id}
                  onChange={(e) => {
                    setFormData({ ...formData, shopify_location_id: e.target.value });
                    setSelectedShopifyId(null);
                    setShopifyFill(null);
                    setLinkedWarning(null);
                    setShopifyIdError(null);
                  }}
                  required
                  disabled={isLoading}
                  readOnly={!isEditing && selectedShopifyId !== null}
                  aria-invalid={shopifyIdError !== null || undefined}
                  aria-describedby={shopifyIdError !== null ? shopifyIdErrorId : undefined}
                  className={cn(
                    !isEditing && selectedShopifyId !== null && 'bg-muted font-mono pr-9',
                    shopifyIdError !== null &&
                      'border-destructive ring-1 ring-destructive focus-visible:border-destructive'
                  )}
                />
                {!isEditing && selectedShopifyId !== null && (
                  <button
                    type="button"
                    onClick={clearShopifySelection}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
                    aria-label="Clear Shopify location selection"
                    title="Clear selection"
                    disabled={isLoading}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {shopifyIdError !== null && (
                <p id={shopifyIdErrorId} role="alert" className="text-sm text-destructive">
                  {shopifyIdError}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Find this in Shopify Admin → Settings → Locations
              </p>
            </div>

            <SectionHeading>Address</SectionHeading>

            <div className="space-y-2">
              <Label htmlFor="address_line1">Address line 1</Label>
              <Input
                id="address_line1"
                type="text"
                placeholder="e.g., 12 Warehouse Rd"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                maxLength={200}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2">Address line 2</Label>
              <Input
                id="address_line2"
                type="text"
                placeholder="e.g., Unit 4, Building B"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                maxLength={200}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="city">City *</Label>
                {renderModifiedTag('city', 'city')}
              </div>
              <Input
                id="city"
                type="text"
                placeholder="e.g., New York"
                value={formData.city}
                onChange={(e) => {
                  setFormData({ ...formData, city: e.target.value });
                  // Any edit to the city implies the user wants auto-detect to
                  // reconsider — even in edit mode.
                  if (isEditing) setTimezoneManuallySet(false);
                }}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="state_province">State / province</Label>
                <Input
                  id="state_province"
                  type="text"
                  placeholder="e.g., New York"
                  value={formData.state_province}
                  onChange={(e) => setFormData({ ...formData, state_province: e.target.value })}
                  maxLength={120}
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal code</Label>
                <Input
                  id="postal_code"
                  type="text"
                  placeholder="e.g., 10001"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  maxLength={60}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Country *</Label>
                {renderModifiedTag('country_code', 'country')}
              </div>
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={countryOpen}
                    aria-controls={countryListboxId}
                    aria-haspopup="listbox"
                    className="w-full justify-between font-normal"
                    disabled={isLoading}
                  >
                    <span className="truncate">
                      {selectedCountry ? selectedCountry.name : 'Select country'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContentInline
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Search country…" />
                    <CommandList id={countryListboxId}>
                      <CommandEmpty>No country matches that search.</CommandEmpty>
                      <CommandGroup heading="Common">
                        {commonCountries.map((country) => (
                          <CommandItem
                            key={`common-${country.code}`}
                            value={`${country.name} ${country.code}`}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, country_code: country.code }));
                              setTimezoneManuallySet(false);
                              setCountryOpen(false);
                            }}
                            className="flex items-center gap-2"
                          >
                            <span className="flex-1 truncate">{country.name}</span>
                            <span className="text-xs text-muted-foreground font-mono tabular-nums">
                              {country.code}
                            </span>
                            {formData.country_code === country.code && (
                              <Check className="h-4 w-4 shrink-0" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="All countries">
                        {otherCountries.map((country) => (
                          <CommandItem
                            key={country.code}
                            value={`${country.name} ${country.code}`}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, country_code: country.code }));
                              setTimezoneManuallySet(false);
                              setCountryOpen(false);
                            }}
                            className="flex items-center gap-2"
                          >
                            <span className="flex-1 truncate">{country.name}</span>
                            <span className="text-xs text-muted-foreground font-mono tabular-nums">
                              {country.code}
                            </span>
                            {formData.country_code === country.code && (
                              <Check className="h-4 w-4 shrink-0" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContentInline>
              </Popover>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Timezone *</Label>
                {timezoneSourceLabel && (
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {timezoneSourceLabel}
                  </span>
                )}
              </div>
              <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    aria-controls={timezoneListboxId}
                    aria-haspopup="listbox"
                    className="w-full justify-between font-normal h-auto py-2"
                    disabled={isLoading || isDetectingTz}
                  >
                    <span className="flex items-center gap-2 min-w-0 text-left">
                      {isDetectingTz ? (
                        <>
                          <TickingClock className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Detecting timezone…
                          </span>
                        </>
                      ) : formData.timezone ? (
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-sm">
                            {humanizeZoneCity(formData.timezone)}
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground font-mono tabular-nums">
                            {getZoneOffset(formData.timezone)}
                            {getZoneOffset(formData.timezone) ? ' · ' : ''}
                            {formData.timezone}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm">Select timezone</span>
                      )}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContentInline
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Search timezone…" />
                    <CommandList id={timezoneListboxId}>
                      <CommandEmpty>No timezone matches that search.</CommandEmpty>
                      <CommandGroup heading="Common">
                        {commonTimezones.map((tz) => (
                          <CommandItem
                            key={`common-${tz}`}
                            value={`${humanizeZoneCity(tz)} ${tz}`}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, timezone: tz }));
                              setTimezoneManuallySet(true);
                              setDetectionSource(null);
                              setTimezoneOpen(false);
                            }}
                            className="flex items-center gap-2"
                          >
                            {renderTimezoneRow(tz)}
                            {formData.timezone === tz && <Check className="h-4 w-4 shrink-0" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="All timezones">
                        {otherTimezones.map((tz) => (
                          <CommandItem
                            key={tz}
                            value={`${humanizeZoneCity(tz)} ${tz}`}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, timezone: tz }));
                              setTimezoneManuallySet(true);
                              setDetectionSource(null);
                              setTimezoneOpen(false);
                            }}
                            className="flex items-center gap-2"
                          >
                            {renderTimezoneRow(tz)}
                            {formData.timezone === tz && <Check className="h-4 w-4 shrink-0" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContentInline>
              </Popover>
            </div>

            <SectionHeading>Contact</SectionHeading>

            <div className="space-y-2">
              <Label htmlFor="contact_phone">Phone</Label>
              <Input
                id="contact_phone"
                type="tel"
                placeholder="e.g., +1 212 555 0100"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                maxLength={60}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">Emails</Label>
              {formData.contact_emails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.contact_emails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 rounded-full border border-threadheads-purple/30 bg-threadheads-purple/10 px-3 py-1 text-sm text-threadheads-purple"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        aria-label={`Remove ${email}`}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-threadheads-purple/20"
                        disabled={isLoading}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  id="contact_email"
                  type="email"
                  placeholder="e.g., ops@warehouse.com"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? emailErrorId : emailHelpId}
                  disabled={isLoading || formData.contact_emails.length >= MAX_CONTACT_EMAILS}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddEmail}
                  disabled={
                    isLoading ||
                    !emailInput.trim() ||
                    formData.contact_emails.length >= MAX_CONTACT_EMAILS
                  }
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                  Add
                </Button>
              </div>
              {emailError ? (
                <p id={emailErrorId} className="text-sm text-destructive">
                  {emailError}
                </p>
              ) : (
                <p id={emailHelpId} className="text-xs text-muted-foreground">
                  Press Enter or Add to save each email. Add up to {MAX_CONTACT_EMAILS}.
                </p>
              )}
            </div>

            <SectionHeading>Customs</SectionHeading>

            <div className="space-y-2">
              <Label htmlFor="customs_info">Customs information</Label>
              <Textarea
                id="customs_info"
                placeholder="Importer of record, customs codes, tax IDs, and any clearance notes."
                value={formData.customs_info}
                onChange={(e) => setFormData({ ...formData, customs_info: e.target.value })}
                maxLength={2000}
                disabled={isLoading}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Free text. Shown to fulfilment and shipping teams for cross-border shipments.
              </p>
            </div>

            {/* BLU-495 Q4: `enable_auto_availability` toggle hidden 2026-05-25.
                The back-sync architecture this gate fed was deprecated in favor
                of `safetyBufferPercent` on the push worker. The DB column is
                retained for rollback; cleanup tracked at BLU-XXX-cleanup-
                availability-vestigial. The formData field is still populated
                from the location row so saves preserve the existing value. */}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? 'Updating…' : 'Creating…'}
                </>
              ) : isEditing ? (
                'Update Location'
              ) : (
                'Create Location'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
